export interface StepOption {
  value: string
  desc?: string
  icon: string
  iconColor?: string
}

export interface FormStep {
  field: string
  question: string
  layout: 'list' | 'grid-2' | 'grid-3'
  options: StepOption[]
}

export interface PXFormConfig {
  slug: string
  formName: string
  taskName: string
  displayName: string
  specialistTitle: string
  steps: FormStep[]
}

const timelineOptions: StepOption[] = [
  { value: 'Immediately', icon: 'ClockIcon', iconColor: 'text-red-500' },
  { value: 'Within 1 Month', icon: 'ClockIcon', iconColor: 'text-amber-500' },
  { value: '1-3 Months', icon: 'ClockIcon', iconColor: 'text-blue-500' },
  { value: 'Just Planning', icon: 'ClockIcon', iconColor: 'text-slate-500' },
]

export const pxFormConfigs: Record<string, PXFormConfig> = {
  // ─── ELECTRICAL ──────────────────────────────────────────────────────
  // PX Fields: ElectricType, ElectricSubType, Timeline
  electrical: {
    slug: 'electrical',
    formName: 'electrical_quote_form_v1',
    taskName: 'Electrical',
    displayName: 'Electrical',
    specialistTitle: 'electrician',
    steps: [
      {
        field: 'electric_type',
        question: 'What type of electrical work do you need?',
        layout: 'list',
        options: [
          {
            value: 'Repair',
            desc: 'Fix an existing electrical issue',
            icon: 'WrenchIcon',
          },
          {
            value: 'Installation',
            desc: 'Install new electrical components',
            icon: 'HammerIcon',
          },
          {
            value: 'Upgrade',
            desc: 'Upgrade panel, wiring, or capacity',
            icon: 'ZapIcon',
          },
          {
            value: 'Inspection',
            desc: 'Full electrical safety inspection',
            icon: 'SearchIcon',
          },
          {
            value: 'Other',
            desc: 'Other electrical work needed',
            icon: 'HelpCircleIcon',
          },
        ],
      },
      {
        field: 'electric_sub_type',
        question: 'What specifically do you need help with?',
        layout: 'list',
        options: [
          {
            value: 'Wiring / Rewiring',
            desc: 'New wiring or replacing old wiring',
            icon: 'ZapIcon',
          },
          {
            value: 'Panel / Breaker Box',
            desc: 'Electrical panel upgrade or repair',
            icon: 'ZapIcon',
          },
          {
            value: 'Lighting',
            desc: 'Install or repair lighting fixtures',
            icon: 'ZapIcon',
          },
          {
            value: 'Outlets / Switches',
            desc: 'Add, move, or repair outlets and switches',
            icon: 'WrenchIcon',
          },
          {
            value: 'Ceiling Fan',
            desc: 'Install or repair ceiling fans',
            icon: 'ZapIcon',
          },
          {
            value: 'Generator',
            desc: 'Whole-home or portable generator installation',
            icon: 'ZapIcon',
          },
          {
            value: 'EV Charger',
            desc: 'Electric vehicle charger installation',
            icon: 'ZapIcon',
          },
          {
            value: 'Other',
            desc: 'Other electrical work',
            icon: 'HelpCircleIcon',
          },
        ],
      },
      {
        field: 'timeline',
        question: 'When do you need this done?',
        layout: 'grid-2',
        options: [
          {
            value: 'Emergency / ASAP',
            icon: 'ClockIcon',
            iconColor: 'text-red-500',
          },
          {
            value: 'Within 1 Month',
            icon: 'ClockIcon',
            iconColor: 'text-amber-500',
          },
          {
            value: '1-3 Months',
            icon: 'ClockIcon',
            iconColor: 'text-blue-500',
          },
          {
            value: 'Just Planning',
            icon: 'ClockIcon',
            iconColor: 'text-slate-500',
          },
        ],
      },
    ],
  },

  // ─── DOORS ───────────────────────────────────────────────────────────
  // PX Fields: DoorType, DoorProject, NumberOfDoors, Timeline
  doors: {
    slug: 'doors',
    formName: 'doors_quote_form_v1',
    taskName: 'Doors',
    displayName: 'Door',
    specialistTitle: 'door installation specialist',
    steps: [
      {
        field: 'door_type',
        question: 'What type of door do you need help with?',
        layout: 'list',
        options: [
          {
            value: 'Front / Entry Door',
            desc: 'Main entry or front door',
            icon: 'DoorOpenIcon',
          },
          {
            value: 'Patio / Sliding Door',
            desc: 'Sliding glass or patio door',
            icon: 'DoorOpenIcon',
          },
          {
            value: 'Interior Doors',
            desc: 'Bedroom, bathroom, or closet doors',
            icon: 'DoorOpenIcon',
          },
          {
            value: 'Storm Door',
            desc: 'Storm or screen door',
            icon: 'ShieldIcon',
          },
          {
            value: 'French Doors',
            desc: 'Double doors for interior or exterior',
            icon: 'DoorOpenIcon',
          },
          {
            value: 'Garage Door',
            desc: 'Garage door installation or repair',
            icon: 'WarehouseIcon',
          },
        ],
      },
      {
        field: 'door_project',
        question: 'What type of work is needed?',
        layout: 'list',
        options: [
          {
            value: 'Install New',
            desc: 'Install a door where there is none',
            icon: 'HammerIcon',
          },
          {
            value: 'Replace Existing',
            desc: 'Replace a current door with a new one',
            icon: 'RefreshCwIcon',
          },
          {
            value: 'Repair',
            desc: 'Fix an existing door or hardware',
            icon: 'WrenchIcon',
          },
        ],
      },
      {
        field: 'door_count',
        question: 'How many doors are involved?',
        layout: 'grid-3',
        options: [
          { value: '1', icon: 'none' },
          { value: '2-3', icon: 'none' },
          { value: '4+', icon: 'none' },
        ],
      },
      {
        field: 'timeline',
        question: 'When do you need this done?',
        layout: 'grid-2',
        options: timelineOptions,
      },
    ],
  },

  // ─── STAIRLIFTS ──────────────────────────────────────────────────────
  // PX Fields: StairliftType, StairliftReason, NumberOfFloors, Timeline
  stairlifts: {
    slug: 'stairlifts',
    formName: 'stairlifts_quote_form_v1',
    taskName: 'Stairlifts',
    displayName: 'Stairlift',
    specialistTitle: 'stairlift specialist',
    steps: [
      {
        field: 'stairlift_type',
        question: 'What type of stairlift do you need?',
        layout: 'list',
        options: [
          {
            value: 'Straight Stairlift',
            desc: 'For a straight staircase',
            icon: 'ArrowUpIcon',
          },
          {
            value: 'Curved Stairlift',
            desc: 'For stairs with turns or landings',
            icon: 'ArrowUpIcon',
          },
          {
            value: 'Outdoor Stairlift',
            desc: 'For exterior stairs or porches',
            icon: 'ArrowUpIcon',
          },
          {
            value: 'Repair / Service',
            desc: 'Repair or maintain an existing stairlift',
            icon: 'WrenchIcon',
          },
        ],
      },
      {
        field: 'stairlift_reason',
        question: 'What is the primary reason for the stairlift?',
        layout: 'list',
        options: [
          {
            value: 'Mobility Issues',
            desc: 'Difficulty walking or climbing stairs',
            icon: 'ArrowUpIcon',
          },
          {
            value: 'Aging in Place',
            desc: 'Planning ahead for future mobility needs',
            icon: 'HomeIcon',
          },
          {
            value: 'Injury Recovery',
            desc: 'Temporary need due to injury or surgery',
            icon: 'ArrowUpIcon',
          },
          {
            value: 'Disability',
            desc: 'Permanent disability or condition',
            icon: 'ArrowUpIcon',
          },
          { value: 'Other', desc: 'Other reason', icon: 'HelpCircleIcon' },
        ],
      },
      {
        field: 'number_of_floors',
        question: 'How many floors does your home have?',
        layout: 'grid-3',
        options: [
          { value: '2 Floors', icon: 'none' },
          { value: '3 Floors', icon: 'none' },
          { value: '4+ Floors', icon: 'none' },
        ],
      },
      {
        field: 'timeline',
        question: 'When do you need this done?',
        layout: 'grid-2',
        options: timelineOptions,
      },
    ],
  },

  // ─── BATHROOM REMODEL ────────────────────────────────────────────────
  // PX Fields: BathroomProject, NumberOfBathrooms, Timeline
  'bathroom-remodel': {
    slug: 'bathroom-remodel',
    formName: 'bathroom_remodel_quote_form_v1',
    taskName: 'Bathroom Remodel',
    displayName: 'Bathroom Remodel',
    specialistTitle: 'bathroom remodeling specialist',
    steps: [
      {
        field: 'project_scope',
        question: 'What does your bathroom project involve?',
        layout: 'list',
        options: [
          {
            value: 'Full Remodel',
            desc: 'Complete bathroom renovation',
            icon: 'ShowerHeadIcon',
          },
          {
            value: 'Shower / Tub Only',
            desc: 'Replace or upgrade shower or tub',
            icon: 'BathIcon',
          },
          {
            value: 'Vanity & Fixtures',
            desc: 'New vanity, faucets, or fixtures',
            icon: 'SparklesIcon',
          },
          {
            value: 'Tile & Flooring',
            desc: 'New tile, flooring, or backsplash',
            icon: 'FootprintsIcon',
          },
          {
            value: 'Accessibility Upgrade',
            desc: 'Grab bars, walk-in features, ADA compliance',
            icon: 'ArrowUpIcon',
          },
        ],
      },
      {
        field: 'bathroom_count',
        question: 'How many bathrooms are involved?',
        layout: 'grid-3',
        options: [
          { value: '1', icon: 'none' },
          { value: '2', icon: 'none' },
          { value: '3+', icon: 'none' },
        ],
      },
      {
        field: 'timeline',
        question: 'When do you need this done?',
        layout: 'grid-2',
        options: timelineOptions,
      },
    ],
  },

  // ─── SOLAR ───────────────────────────────────────────────────────────
  // PX Fields: SolarProject, RoofShade, MonthlyElectricBill, ElectricUtility, RoofType, CreditScore, Timeline
  solar: {
    slug: 'solar',
    formName: 'solar_quote_form_v1',
    taskName: 'Solar',
    displayName: 'Solar',
    specialistTitle: 'solar energy specialist',
    steps: [
      {
        field: 'project_type',
        question: 'What solar project are you interested in?',
        layout: 'list',
        options: [
          {
            value: 'Rooftop Solar Panels',
            desc: 'Install solar panels on your roof',
            icon: 'SunIcon',
          },
          {
            value: 'Solar Battery Storage',
            desc: 'Add battery backup for your solar system',
            icon: 'ZapIcon',
          },
          {
            value: 'Solar Roof Tiles',
            desc: 'Integrated solar roofing tiles',
            icon: 'SunIcon',
          },
          {
            value: 'Consultation / Quote',
            desc: 'Just exploring options and pricing',
            icon: 'SearchIcon',
          },
        ],
      },
      {
        field: 'roof_type',
        question: 'What type of roof do you have?',
        layout: 'grid-2',
        options: [
          { value: 'Asphalt Shingle', icon: 'LayersIcon' },
          { value: 'Metal', icon: 'ShieldIcon' },
          { value: 'Tile / Clay', icon: 'HomeIcon' },
          { value: 'Flat', icon: 'SquareIcon' },
          { value: 'Wood Shake', icon: 'TreePineIcon' },
          { value: 'Not Sure', icon: 'HelpCircleIcon' },
        ],
      },
      {
        field: 'roof_shade',
        question: 'How much shade does your roof get?',
        layout: 'list',
        options: [
          {
            value: 'Full Sun',
            desc: 'Little to no shade throughout the day',
            icon: 'SunIcon',
          },
          {
            value: 'Partial Shade',
            desc: 'Some shade from trees or buildings',
            icon: 'TreesIcon',
          },
          {
            value: 'Heavy Shade',
            desc: 'Significant shade coverage',
            icon: 'TreesIcon',
          },
          {
            value: 'Not Sure',
            desc: 'I am not sure about shade levels',
            icon: 'HelpCircleIcon',
          },
        ],
      },
      {
        field: 'monthly_electric_bill',
        question: 'What is your average monthly electric bill?',
        layout: 'grid-2',
        options: [
          { value: 'Under $100', icon: 'ZapIcon' },
          { value: '$100 - $200', icon: 'ZapIcon' },
          { value: '$200 - $300', icon: 'ZapIcon' },
          { value: '$300+', icon: 'ZapIcon' },
        ],
      },
      {
        field: 'credit_score',
        question: 'What is your approximate credit score?',
        layout: 'grid-2',
        options: [
          { value: 'Excellent (720+)', icon: 'CheckCircle2Icon' },
          { value: 'Good (680-719)', icon: 'CheckCircle2Icon' },
          { value: 'Fair (620-679)', icon: 'CheckCircle2Icon' },
          { value: 'Below 620', icon: 'CheckCircle2Icon' },
          { value: 'Not Sure', icon: 'HelpCircleIcon' },
        ],
      },
      {
        field: 'timeline',
        question: 'When do you need this done?',
        layout: 'grid-2',
        options: timelineOptions,
      },
    ],
  },

  // ─── KITCHEN REMODEL ─────────────────────────────────────────────────
  // PX Fields: KitchenProject, KitchenSize, Timeline
  'kitchen-remodel': {
    slug: 'kitchen-remodel',
    formName: 'kitchen_remodel_quote_form_v1',
    taskName: 'Kitchen Remodel',
    displayName: 'Kitchen Remodel',
    specialistTitle: 'kitchen remodeling specialist',
    steps: [
      {
        field: 'project_scope',
        question: 'What does your kitchen project involve?',
        layout: 'list',
        options: [
          {
            value: 'Full Remodel',
            desc: 'Complete kitchen renovation',
            icon: 'CookingPotIcon',
          },
          {
            value: 'Cabinets & Countertops',
            desc: 'Replace or reface cabinets and counters',
            icon: 'CookingPotIcon',
          },
          {
            value: 'Appliance Installation',
            desc: 'Install new kitchen appliances',
            icon: 'HammerIcon',
          },
          {
            value: 'Backsplash & Tile',
            desc: 'New backsplash or tile work',
            icon: 'FootprintsIcon',
          },
          {
            value: 'Flooring Only',
            desc: 'Kitchen flooring replacement',
            icon: 'FootprintsIcon',
          },
        ],
      },
      {
        field: 'kitchen_size',
        question: 'How would you describe your kitchen size?',
        layout: 'grid-3',
        options: [
          { value: 'Small', icon: 'none' },
          { value: 'Medium', icon: 'none' },
          { value: 'Large', icon: 'none' },
        ],
      },
      {
        field: 'timeline',
        question: 'When do you need this done?',
        layout: 'grid-2',
        options: timelineOptions,
      },
    ],
  },

  // ─── HVAC ────────────────────────────────────────────────────────────
  // PX Fields: HVACType, HVACProject, SystemAge, FuelType, Timeline
  hvac: {
    slug: 'hvac',
    formName: 'hvac_quote_form_v1',
    taskName: 'HVAC',
    displayName: 'HVAC',
    specialistTitle: 'HVAC technician',
    steps: [
      {
        field: 'system_type',
        question: 'What type of HVAC system do you need help with?',
        layout: 'list',
        options: [
          {
            value: 'Air Conditioning',
            desc: 'Central AC, window units, or mini-splits',
            icon: 'ThermometerIcon',
          },
          {
            value: 'Heating',
            desc: 'Furnace, heat pump, or boiler',
            icon: 'ThermometerIcon',
          },
          {
            value: 'Both Heating & Cooling',
            desc: 'Full HVAC system',
            icon: 'ThermometerIcon',
          },
          {
            value: 'Ductless Mini-Split',
            desc: 'Ductless heating and cooling',
            icon: 'ThermometerIcon',
          },
        ],
      },
      {
        field: 'project_type',
        question: 'What type of work is needed?',
        layout: 'list',
        options: [
          {
            value: 'Repair',
            desc: 'Fix an existing system',
            icon: 'WrenchIcon',
          },
          {
            value: 'Replacement',
            desc: 'Replace an old or broken system',
            icon: 'RefreshCwIcon',
          },
          {
            value: 'New Installation',
            desc: 'Install a brand new system',
            icon: 'HammerIcon',
          },
          {
            value: 'Maintenance',
            desc: 'Routine tune-up or inspection',
            icon: 'SearchIcon',
          },
        ],
      },
      {
        field: 'system_age',
        question: 'How old is your current system?',
        layout: 'grid-2',
        options: [
          { value: 'Less than 5 years', icon: 'ClockIcon' },
          { value: '5-10 years', icon: 'ClockIcon' },
          { value: '10-15 years', icon: 'ClockIcon' },
          { value: '15+ years', icon: 'ClockIcon' },
          { value: 'No existing system', icon: 'HelpCircleIcon' },
          { value: 'Not Sure', icon: 'HelpCircleIcon' },
        ],
      },
      {
        field: 'fuel_type',
        question: 'What fuel type does your system use?',
        layout: 'grid-2',
        options: [
          { value: 'Natural Gas', icon: 'ThermometerIcon' },
          { value: 'Electric', icon: 'ZapIcon' },
          { value: 'Oil', icon: 'ThermometerIcon' },
          { value: 'Propane', icon: 'ThermometerIcon' },
          { value: 'Not Sure', icon: 'HelpCircleIcon' },
        ],
      },
      {
        field: 'timeline',
        question: 'When do you need this done?',
        layout: 'grid-2',
        options: [
          {
            value: 'Emergency / ASAP',
            icon: 'ClockIcon',
            iconColor: 'text-red-500',
          },
          {
            value: 'Within 1 Month',
            icon: 'ClockIcon',
            iconColor: 'text-amber-500',
          },
          {
            value: '1-3 Months',
            icon: 'ClockIcon',
            iconColor: 'text-blue-500',
          },
          {
            value: 'Just Planning',
            icon: 'ClockIcon',
            iconColor: 'text-slate-500',
          },
        ],
      },
    ],
  },

  // ─── FLOORING ────────────────────────────────────────────────────────
  // PX Fields: FlooringType, FlooringProject, NumberOfRooms, TotalSqFt, Timeline
  flooring: {
    slug: 'flooring',
    formName: 'flooring_quote_form_v1',
    taskName: 'Flooring',
    displayName: 'Flooring',
    specialistTitle: 'flooring specialist',
    steps: [
      {
        field: 'flooring_type',
        question: 'What type of flooring are you interested in?',
        layout: 'grid-2',
        options: [
          { value: 'Hardwood', icon: 'FootprintsIcon' },
          { value: 'Tile / Stone', icon: 'FootprintsIcon' },
          { value: 'Carpet', icon: 'FootprintsIcon' },
          { value: 'Vinyl / Laminate', icon: 'FootprintsIcon' },
          { value: 'Engineered Wood', icon: 'FootprintsIcon' },
          { value: 'Not Sure', icon: 'HelpCircleIcon' },
        ],
      },
      {
        field: 'flooring_project',
        question: 'What type of flooring work do you need?',
        layout: 'list',
        options: [
          {
            value: 'Install New',
            desc: 'Install flooring in a new or bare space',
            icon: 'HammerIcon',
          },
          {
            value: 'Replace Existing',
            desc: 'Remove old flooring and install new',
            icon: 'RefreshCwIcon',
          },
          {
            value: 'Refinish',
            desc: 'Sand and refinish existing hardwood floors',
            icon: 'SparklesIcon',
          },
          {
            value: 'Repair',
            desc: 'Fix damaged sections of flooring',
            icon: 'WrenchIcon',
          },
        ],
      },
      {
        field: 'room_count',
        question: 'How many rooms need flooring?',
        layout: 'grid-3',
        options: [
          { value: '1 Room', icon: 'none' },
          { value: '2-3 Rooms', icon: 'none' },
          { value: '4+ Rooms', icon: 'none' },
        ],
      },
      {
        field: 'total_sqft',
        question: 'What is the approximate total square footage?',
        layout: 'grid-2',
        options: [
          { value: 'Under 500 sq ft', icon: 'SquareIcon' },
          { value: '500 - 1,000 sq ft', icon: 'SquareIcon' },
          { value: '1,000 - 2,000 sq ft', icon: 'SquareIcon' },
          { value: '2,000+ sq ft', icon: 'SquareIcon' },
          { value: 'Not Sure', icon: 'HelpCircleIcon' },
        ],
      },
      {
        field: 'timeline',
        question: 'When do you need this done?',
        layout: 'grid-2',
        options: timelineOptions,
      },
    ],
  },

  // ─── PEST CONTROL ────────────────────────────────────────────────────
  // PX Fields: PestType, PropertyType, ServiceFrequency, Urgency
  'pest-control': {
    slug: 'pest-control',
    formName: 'pest_control_quote_form_v1',
    taskName: 'Pest Control',
    displayName: 'Pest Control',
    specialistTitle: 'pest control specialist',
    steps: [
      {
        field: 'pest_type',
        question: 'What type of pest issue do you have?',
        layout: 'list',
        options: [
          {
            value: 'General Pest Control',
            desc: 'Ants, roaches, spiders, etc.',
            icon: 'BugIcon',
          },
          {
            value: 'Termites',
            desc: 'Termite inspection or treatment',
            icon: 'BugIcon',
          },
          {
            value: 'Rodents',
            desc: 'Mice, rats, or other rodents',
            icon: 'BugIcon',
          },
          {
            value: 'Bed Bugs',
            desc: 'Bed bug inspection or treatment',
            icon: 'BugIcon',
          },
          {
            value: 'Wildlife Removal',
            desc: 'Raccoons, squirrels, bats, etc.',
            icon: 'BugIcon',
          },
          {
            value: 'Mosquitoes / Ticks',
            desc: 'Yard treatment for mosquitoes or ticks',
            icon: 'BugIcon',
          },
        ],
      },
      {
        field: 'property_type',
        question: 'What type of property is this?',
        layout: 'grid-2',
        options: [
          { value: 'Single Family Home', icon: 'HomeIcon' },
          { value: 'Townhouse / Condo', icon: 'HomeIcon' },
          { value: 'Apartment', icon: 'HomeIcon' },
          { value: 'Commercial', icon: 'HomeIcon' },
        ],
      },
      {
        field: 'service_frequency',
        question: 'What type of service are you looking for?',
        layout: 'list',
        options: [
          {
            value: 'One-Time Treatment',
            desc: 'Single visit to address a specific issue',
            icon: 'CheckCircle2Icon',
          },
          {
            value: 'Monthly Service',
            desc: 'Regular monthly pest prevention',
            icon: 'ClockIcon',
          },
          {
            value: 'Quarterly Service',
            desc: 'Seasonal treatments every 3 months',
            icon: 'ClockIcon',
          },
          {
            value: 'Annual Plan',
            desc: 'Year-round protection plan',
            icon: 'ShieldIcon',
          },
        ],
      },
      {
        field: 'urgency',
        question: 'How urgent is this issue?',
        layout: 'list',
        options: [
          {
            value: 'Emergency',
            desc: 'I need help immediately',
            icon: 'ClockIcon',
          },
          {
            value: 'Soon',
            desc: 'Within the next week or two',
            icon: 'ClockIcon',
          },
          {
            value: 'Routine',
            desc: 'Scheduled maintenance or prevention',
            icon: 'ClockIcon',
          },
        ],
      },
    ],
  },

  // ─── WALK-IN TUBS ────────────────────────────────────────────────────
  // PX Fields: WalkInTubProject, WalkInTubFeatures, MobilityNeeds, Timeline
  'walk-in-tubs': {
    slug: 'walk-in-tubs',
    formName: 'walk_in_tub_quote_form_v1',
    taskName: 'Walk-In Tub',
    displayName: 'Walk-In Tub',
    specialistTitle: 'walk-in tub specialist',
    steps: [
      {
        field: 'project_type',
        question: 'What type of walk-in tub project do you need?',
        layout: 'list',
        options: [
          {
            value: 'New Installation',
            desc: 'Install a new walk-in tub',
            icon: 'BathIcon',
          },
          {
            value: 'Replacement / Upgrade',
            desc: 'Replace an existing tub with a walk-in',
            icon: 'RefreshCwIcon',
          },
          {
            value: 'Conversion from Shower',
            desc: 'Convert a shower to a walk-in tub',
            icon: 'ShowerHeadIcon',
          },
          {
            value: 'Repair / Service',
            desc: 'Repair an existing walk-in tub',
            icon: 'WrenchIcon',
          },
        ],
      },
      {
        field: 'mobility_needs',
        question: 'Is this for someone with mobility challenges?',
        layout: 'list',
        options: [
          {
            value: 'Yes - Current Mobility Issues',
            desc: 'Difficulty getting in/out of a standard tub',
            icon: 'ArrowUpIcon',
          },
          {
            value: 'Yes - Planning Ahead',
            desc: 'Preparing for future mobility needs',
            icon: 'HomeIcon',
          },
          {
            value: 'No - Comfort / Luxury',
            desc: 'Want the comfort features of a walk-in tub',
            icon: 'BathIcon',
          },
        ],
      },
      {
        field: 'features_needed',
        question: 'What features are most important to you?',
        layout: 'grid-2',
        options: [
          { value: 'Hydrotherapy Jets', icon: 'BathIcon' },
          { value: 'Heated Seat', icon: 'ThermometerIcon' },
          { value: 'Quick Drain', icon: 'BathIcon' },
          { value: 'Low Step-In', icon: 'ArrowUpIcon' },
          { value: 'Handheld Shower', icon: 'ShowerHeadIcon' },
          { value: 'Not Sure', icon: 'HelpCircleIcon' },
        ],
      },
      {
        field: 'timeline',
        question: 'When do you need this done?',
        layout: 'grid-2',
        options: timelineOptions,
      },
    ],
  },

  // ─── SIDING ──────────────────────────────────────────────────────────
  // PX Fields: SidingProject, SidingMaterial, NumberOfStories, Timeline
  siding: {
    slug: 'siding',
    formName: 'siding_quote_form_v1',
    taskName: 'Siding',
    displayName: 'Siding',
    specialistTitle: 'siding specialist',
    steps: [
      {
        field: 'project_type',
        question: 'What type of siding project do you need?',
        layout: 'list',
        options: [
          {
            value: 'Full Replacement',
            desc: 'Replace all siding on the home',
            icon: 'RefreshCwIcon',
          },
          {
            value: 'Partial Repair',
            desc: 'Fix damaged sections',
            icon: 'WrenchIcon',
          },
          {
            value: 'New Installation',
            desc: 'Siding for new construction or addition',
            icon: 'HammerIcon',
          },
          {
            value: 'Inspection / Estimate',
            desc: 'Professional assessment',
            icon: 'SearchIcon',
          },
        ],
      },
      {
        field: 'siding_material',
        question: 'What siding material do you prefer?',
        layout: 'grid-2',
        options: [
          { value: 'Vinyl', icon: 'HomeIcon' },
          { value: 'Fiber Cement', icon: 'HomeIcon' },
          { value: 'Wood', icon: 'TreePineIcon' },
          { value: 'Metal / Aluminum', icon: 'ShieldIcon' },
          { value: 'Stucco', icon: 'HomeIcon' },
          { value: 'Not Sure', icon: 'HelpCircleIcon' },
        ],
      },
      {
        field: 'stories',
        question: 'How many stories is your home?',
        layout: 'grid-3',
        options: [
          { value: '1 Story', icon: 'none' },
          { value: '2 Stories', icon: 'none' },
          { value: '3+ Stories', icon: 'none' },
        ],
      },
      {
        field: 'timeline',
        question: 'When do you need this done?',
        layout: 'grid-2',
        options: timelineOptions,
      },
    ],
  },

  // ─── PLUMBING ────────────────────────────────────────────────────────
  // PX Fields: PlumbingType, PlumbingSubType, Urgency, Timeline
  plumbing: {
    slug: 'plumbing',
    formName: 'plumbing_quote_form_v1',
    taskName: 'Plumbing',
    displayName: 'Plumbing',
    specialistTitle: 'plumber',
    steps: [
      {
        field: 'project_type',
        question: 'What type of plumbing work do you need?',
        layout: 'list',
        options: [
          {
            value: 'Repair / Fix Issue',
            desc: 'Leaks, clogs, or broken fixtures',
            icon: 'WrenchIcon',
          },
          {
            value: 'New Installation',
            desc: 'New fixtures, pipes, or appliances',
            icon: 'HammerIcon',
          },
          {
            value: 'Drain Cleaning',
            desc: 'Clear clogged or slow drains',
            icon: 'SparklesIcon',
          },
          {
            value: 'Water Heater',
            desc: 'Install, repair, or replace water heater',
            icon: 'ThermometerIcon',
          },
          {
            value: 'Repiping',
            desc: 'Replace old or corroded pipes',
            icon: 'WrenchIcon',
          },
          {
            value: 'Sewer Line',
            desc: 'Sewer line repair or replacement',
            icon: 'WrenchIcon',
          },
        ],
      },
      {
        field: 'plumbing_detail',
        question: 'Can you describe the issue more specifically?',
        layout: 'list',
        options: [
          {
            value: 'Faucet / Sink',
            desc: 'Kitchen or bathroom faucet or sink issue',
            icon: 'WrenchIcon',
          },
          {
            value: 'Toilet',
            desc: 'Toilet repair, replacement, or installation',
            icon: 'WrenchIcon',
          },
          {
            value: 'Shower / Bathtub',
            desc: 'Shower or tub plumbing issue',
            icon: 'ShowerHeadIcon',
          },
          {
            value: 'Pipe Leak / Burst',
            desc: 'Leaking or burst pipe',
            icon: 'WrenchIcon',
          },
          {
            value: 'Garbage Disposal',
            desc: 'Disposal repair or installation',
            icon: 'WrenchIcon',
          },
          {
            value: 'Other / General',
            desc: 'Other plumbing needs',
            icon: 'HelpCircleIcon',
          },
        ],
      },
      {
        field: 'urgency',
        question: 'How urgent is this issue?',
        layout: 'list',
        options: [
          {
            value: 'Emergency',
            desc: 'Active leak, flooding, or no water',
            icon: 'ClockIcon',
          },
          { value: 'Soon', desc: 'Within the next week', icon: 'ClockIcon' },
          {
            value: 'Not Urgent',
            desc: 'Can be scheduled at convenience',
            icon: 'ClockIcon',
          },
        ],
      },
      {
        field: 'timeline',
        question: 'When do you need this done?',
        layout: 'grid-2',
        options: [
          {
            value: 'Emergency / ASAP',
            icon: 'ClockIcon',
            iconColor: 'text-red-500',
          },
          {
            value: 'Within 1 Month',
            icon: 'ClockIcon',
            iconColor: 'text-amber-500',
          },
          {
            value: '1-3 Months',
            icon: 'ClockIcon',
            iconColor: 'text-blue-500',
          },
          {
            value: 'Just Planning',
            icon: 'ClockIcon',
            iconColor: 'text-slate-500',
          },
        ],
      },
    ],
  },

  // ─── HOME SECURITY ───────────────────────────────────────────────────
  // PX Fields: SecurityType, MonitoringPreference, NumberOfEntryPoints, Timeline
  'home-security': {
    slug: 'home-security',
    formName: 'home_security_quote_form_v1',
    taskName: 'Home Security',
    displayName: 'Home Security',
    specialistTitle: 'home security specialist',
    steps: [
      {
        field: 'system_type',
        question: 'What type of security system are you interested in?',
        layout: 'list',
        options: [
          {
            value: 'Full Security System',
            desc: 'Complete home security with monitoring',
            icon: 'ShieldIcon',
          },
          {
            value: 'Camera System',
            desc: 'Indoor/outdoor security cameras',
            icon: 'ShieldIcon',
          },
          {
            value: 'Smart Locks',
            desc: 'Keyless entry and smart lock installation',
            icon: 'DoorOpenIcon',
          },
          {
            value: 'Alarm System',
            desc: 'Burglar alarm or intrusion detection',
            icon: 'ShieldIcon',
          },
          {
            value: 'Video Doorbell',
            desc: 'Smart doorbell with camera',
            icon: 'DoorOpenIcon',
          },
        ],
      },
      {
        field: 'monitoring_preference',
        question: 'Do you want professional monitoring?',
        layout: 'list',
        options: [
          {
            value: 'Yes, Professional Monitoring',
            desc: '24/7 monitoring by a security company',
            icon: 'ShieldIcon',
          },
          {
            value: 'Self-Monitoring',
            desc: 'I will monitor via app on my phone',
            icon: 'HomeIcon',
          },
          {
            value: 'Not Sure',
            desc: 'I would like to learn more about options',
            icon: 'HelpCircleIcon',
          },
        ],
      },
      {
        field: 'entry_points',
        question: 'How many doors and windows need to be secured?',
        layout: 'grid-3',
        options: [
          { value: '1-5', icon: 'none' },
          { value: '6-10', icon: 'none' },
          { value: '10+', icon: 'none' },
        ],
      },
      {
        field: 'timeline',
        question: 'When do you need this done?',
        layout: 'grid-2',
        options: timelineOptions,
      },
    ],
  },

  // ─── GUTTERS ─────────────────────────────────────────────────────────
  // PX Fields: GutterProject, GutterMaterial, NumberOfStories, LinearFeet, Timeline
  'gutter-guards': {
    slug: 'gutter-guards',
    formName: 'gutters_quote_form_v1',
    taskName: 'Gutters',
    displayName: 'Gutter',
    specialistTitle: 'gutter specialist',
    steps: [
      {
        field: 'project_type',
        question: 'What type of gutter project do you need?',
        layout: 'list',
        options: [
          {
            value: 'New Gutter Guards',
            desc: 'Install gutter protection system',
            icon: 'LayersIcon',
          },
          {
            value: 'Gutter Replacement',
            desc: 'Replace old or damaged gutters',
            icon: 'RefreshCwIcon',
          },
          {
            value: 'Gutter Repair',
            desc: 'Fix leaks, sagging, or damage',
            icon: 'WrenchIcon',
          },
          {
            value: 'Cleaning & Maintenance',
            desc: 'Clean out gutters and downspouts',
            icon: 'SparklesIcon',
          },
        ],
      },
      {
        field: 'gutter_material',
        question: 'What gutter material do you prefer?',
        layout: 'grid-2',
        options: [
          { value: 'Aluminum', icon: 'LayersIcon' },
          { value: 'Vinyl', icon: 'LayersIcon' },
          { value: 'Steel', icon: 'ShieldIcon' },
          { value: 'Copper', icon: 'LayersIcon' },
          { value: 'Not Sure', icon: 'HelpCircleIcon' },
        ],
      },
      {
        field: 'stories',
        question: 'How many stories is your home?',
        layout: 'grid-3',
        options: [
          { value: '1 Story', icon: 'none' },
          { value: '2 Stories', icon: 'none' },
          { value: '3+ Stories', icon: 'none' },
        ],
      },
      {
        field: 'linear_feet',
        question: 'Approximately how many linear feet of gutters?',
        layout: 'grid-2',
        options: [
          { value: 'Under 100 ft', icon: 'SquareIcon' },
          { value: '100 - 200 ft', icon: 'SquareIcon' },
          { value: '200 - 300 ft', icon: 'SquareIcon' },
          { value: '300+ ft', icon: 'SquareIcon' },
          { value: 'Not Sure', icon: 'HelpCircleIcon' },
        ],
      },
      {
        field: 'timeline',
        question: 'When do you need this done?',
        layout: 'grid-2',
        options: timelineOptions,
      },
    ],
  },
}
